import React, { ReactElement, useMemo, useState } from 'react';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { Box, Checkbox, Stack, Tooltip, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import styles from 'app/editor/brand-theme/metadata-settings/MetadataSettings.styles';
import { FormComponentBuilder } from 'components/common/form/layout';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import Switch from '@mui/material/Switch/Switch';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import { IElementData, IElementDataSettings, IMetadata } from 'components/common/element-data/types';
import { useSelector } from 'react-redux';
import ElementDataInfo from 'components/common/element-data/ElementDataInfo';
import { prepareMetadataSettings } from 'components/common/element-data/functions';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { useParams } from 'react-router-dom';
import { Params as UrlParams } from '@remix-run/router/dist/utils';
import { metadataSettingsPreviewData } from './MetadataSettingsPreview';

const DragHandleSection = SortableHandle(() => (
    <Box sx={styles.metadataSectionDragIcon}>
        <IconMi icon={'drag-and-drop-grid'} />
    </Box>
));

const SortableSectionsList: any = SortableContainer(({ children }: { children: ReactElement }) => {
    return (
        <Stack spacing={1} sx={styles.metadataSectionsList}>
            {children}
        </Stack>
    );
});

interface MetadataSectionProps {
    data: any;
    sectionIndex: number;
    onSortFieldEnd: () => void;
    onSortFieldStart: () => void;
    onChangeFieldView: (
        view: string,
        value: boolean,
        sectionIndex: number,
        fieldBlockIndex: number,
        fieldIndex: number,
    ) => {};
    onChangeAllFieldsView: (view: string, value: boolean, sectionIndex: number) => {};
}

const fieldsNoteMap: any = {
    thumbnail: 'only_for_multi_column',
    current_value: 'only_for_metric',
    comparison_info: 'only_for_metric',
    moving_average: 'only_for_metric',
    minimum_ever: 'only_for_metric',
    maximum_ever: 'only_for_metric',
    projected: 'only_for_metric',
};

const SortableSectionItem: any = SortableElement((props: MetadataSectionProps) => {
    const { t } = useBundleTranslation(['app/editor/brand-theme/metadata-settings']);
    const {
        data,
        sectionIndex,
        onSortFieldEnd = () => {},
        onSortFieldStart = () => {},
        onChangeFieldView = () => {},
        onChangeAllFieldsView = () => {},
    } = props;
    const isSingleFieldSection = data.fields.length == 1 && data.fields[0].length == 1;
    const sectionTitle = isSingleFieldSection
        ? `metadata.field_title.${data.fields[0][0]['name']}`
        : `metadata.section_title.${data.name}`;
    const sectionNote =
        isSingleFieldSection && fieldsNoteMap[data.fields[0][0]['name']]
            ? `metadata.field_note.${fieldsNoteMap[data.fields[0][0]['name']]}`
            : false;

    const isAllFieldsDisabled = (view: string) => {
        return data.fields.every((fieldBlock: any) => {
            return fieldBlock.every((field: any) => {
                return field.display[view] === null;
            });
        });
    };
    const isAllFieldsChecked = (view: string) => {
        return data.fields.every((fieldBlock: any) => {
            return fieldBlock.every((field: any) => {
                return field.display[view] === true || field.display[view] === null;
            });
        });
    };
    const isAllFieldsNotChecked = (view: string) => {
        return data.fields.every((fieldBlock: any) => {
            return fieldBlock.every((field: any) => {
                return field.display[view] === false || field.display[view] === null;
            });
        });
    };

    return (
        <Stack sx={styles.metadataSection}>
            <Stack direction={'row'} sx={styles.metadataSectionHeading}>
                <DragHandleSection />
                <Box sx={styles.metadataSectionTitle}>
                    {t(sectionTitle)}
                    {sectionNote && (
                        <>
                            {' '}
                            <Box component={'span'} sx={styles.metadataSectionTitleNote}>
                                {t(sectionNote)}
                            </Box>
                        </>
                    )}
                </Box>
                <Box sx={styles.metadataSectionCheckbox}>
                    {isSingleFieldSection ? (
                        <Checkbox
                            checked={data.fields[0][0].display.i}
                            disabled={data.fields[0][0].display.i == null}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                onChangeFieldView('i', event.target.checked, sectionIndex, 0, 0);
                            }}
                        />
                    ) : (
                        <>
                            <Checkbox
                                disabled={isAllFieldsDisabled('i')}
                                checked={!isAllFieldsDisabled('i') && isAllFieldsChecked('i')}
                                indeterminate={!isAllFieldsChecked('i') && !isAllFieldsNotChecked('i')}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onChangeAllFieldsView('i', event.target.checked, sectionIndex);
                                }}
                            />
                        </>
                    )}
                </Box>
                <Box sx={styles.metadataSectionCheckbox}>
                    {isSingleFieldSection ? (
                        <Checkbox
                            checked={data.fields[0][0].display.v}
                            disabled={data.fields[0][0].display.v == null}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                onChangeFieldView('v', event.target.checked, sectionIndex, 0, 0);
                            }}
                        />
                    ) : (
                        <Checkbox
                            disabled={isAllFieldsDisabled('v')}
                            checked={!isAllFieldsDisabled('v') && isAllFieldsChecked('v')}
                            indeterminate={!isAllFieldsChecked('v') && !isAllFieldsNotChecked('v')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                onChangeAllFieldsView('v', event.target.checked, sectionIndex);
                            }}
                        />
                    )}
                </Box>
                <Box sx={styles.metadataSectionCheckbox}>
                    {isSingleFieldSection ? (
                        <Checkbox
                            checked={data.fields[0][0].display.c}
                            disabled={data.fields[0][0].display.c == null}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                onChangeFieldView('c', event.target.checked, sectionIndex, 0, 0);
                            }}
                        />
                    ) : (
                        <Checkbox
                            disabled={isAllFieldsDisabled('c')}
                            checked={!isAllFieldsDisabled('c') && isAllFieldsChecked('c')}
                            indeterminate={!isAllFieldsChecked('c') && !isAllFieldsNotChecked('c')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                onChangeAllFieldsView('c', event.target.checked, sectionIndex);
                            }}
                        />
                    )}
                </Box>
            </Stack>
            {!isSingleFieldSection && (
                <SortableFieldsList
                    onSortEnd={onSortFieldEnd}
                    onSortStart={onSortFieldStart}
                    useDragHandle
                    lockAxis={'y'}
                >
                    {data.fields.map((field: any, index: number) => {
                        return (
                            <SortableFieldItem
                                key={`${data.name}_${index}`}
                                index={index}
                                collection={data.name}
                                data={field}
                                fieldBlockIndex={index}
                                onChangeFieldCheckbox={(view: string, value: boolean, fieldIndex: number) => {
                                    onChangeFieldView(view, value, sectionIndex, index, fieldIndex);
                                }}
                            />
                        );
                    })}
                </SortableFieldsList>
            )}
        </Stack>
    );
});

const DragHandleField = SortableHandle(() => (
    <Box sx={styles.metadataFieldItemDragIcon}>
        <IconMi icon={'drag-and-drop-grid'} />
    </Box>
));

const SortableFieldsList: any = SortableContainer(({ children }: { children: ReactElement }) => {
    return (
        <Box sx={styles.metadataFieldsListContainer}>
            <Stack sx={styles.metadataFieldsList}>{children}</Stack>
        </Box>
    );
});

interface MetadataFieldProps {
    data: any;
    onChangeFieldCheckbox: (view: string, value: boolean, fieldIndex: number) => void;
}

const SortableFieldItem: any = SortableElement((props: MetadataFieldProps) => {
    const { t } = useBundleTranslation(['app/editor/brand-theme/metadata-settings']);
    const { data, onChangeFieldCheckbox = () => {} } = props;
    const ownersConfiguration = useSelector((state: any) => state.auth.configuration.owners);

    const fieldsData: any = useMemo(() => {
        let fieldsBlock = [...data];
        fieldsBlock = fieldsBlock.map((field: any, index: number) => {
            return { ...field, index: index };
        });
        const businessOwnerIndex = data.findIndex((field: any) => field.name == 'business_owner');
        if (businessOwnerIndex !== -1) {
            fieldsBlock.sort((a, b) => {
                return ownersConfiguration[a.name].displayOrder - ownersConfiguration[b.name].displayOrder;
            });
        }
        return fieldsBlock;
    }, [data]);

    return (
        <Box sx={styles.metadataFieldBlock}>
            {fieldsData.map((field: any, fieldIndex: number) => {
                return (
                    <Stack key={field.name} direction={'row'} sx={styles.metadataFieldItem}>
                        {fieldIndex === 0 ? <DragHandleField /> : <Box sx={styles.metadataFieldItemDragIcon} />}
                        <Box sx={styles.metadataFieldTitle}>
                            {t(`metadata.field_title.${field.name}`)}
                            {fieldsNoteMap[field.name] && (
                                <>
                                    {' '}
                                    <Box component={'span'} sx={styles.metadataFieldTitleNote}>
                                        {t(`metadata.field_note.${fieldsNoteMap[field.name]}`)}
                                    </Box>
                                </>
                            )}
                        </Box>
                        <Box sx={styles.metadataFieldCheckbox}>
                            <Checkbox
                                checked={field.display.i}
                                disabled={field.display.i == null}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onChangeFieldCheckbox('i', event.target.checked, field.index);
                                }}
                            />
                        </Box>
                        <Box sx={styles.metadataFieldCheckbox}>
                            <Checkbox
                                checked={field.display.v}
                                disabled={field.display.v == null}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onChangeFieldCheckbox('v', event.target.checked, field.index);
                                }}
                            />
                        </Box>
                        <Box sx={styles.metadataFieldCheckbox}>
                            <Checkbox
                                checked={field.display.c}
                                disabled={field.display.c == null}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onChangeFieldCheckbox('c', event.target.checked, field.index);
                                }}
                            />
                        </Box>
                    </Stack>
                );
            })}
        </Box>
    );
});

export interface FormControlMetadataSettingsProps extends FormControlProps {}

export default function MetadataSettings({
    controlProps,
    elementProps,
}: FormElementControlPropsType<FormControlMetadataSettingsProps>) {
    const { t } = useBundleTranslation(['app/editor/brand-theme/metadata-settings']);
    const [isActiveDrag, setIsActiveDrag] = useState<boolean>(false);
    const [data, setData] = useState<IMetadata>(
        JSON.parse(controlProps.form.hookFormGetValues('brand_theme_tile_element_metadata')),
    );

    const ownersConfiguration = useSelector((state: any) => state.auth.configuration.owners);
    const urlParams: UrlParams = useParams();
    const brandThemeId = Number(urlParams.brandThemeId);

    const updateData = (newData: any) => {
        setData(newData);
        controlProps.form.hookFormSetValue('brand_theme_tile_element_metadata', JSON.stringify(newData), {
            shouldDirty: true,
        });
    };

    const processSort = (oldIndex: number, newIndex: number, collection: string | number) => {
        let newMetadata = [...data.metadata];

        if (collection == 'main_sections') {
            newMetadata = arrayMoveImmutable(newMetadata, oldIndex, newIndex);
            updateData({ ...data, metadata: newMetadata });
        } else {
            const sectionIndex = newMetadata.findIndex((section) => section.name === collection);
            if (sectionIndex !== -1) {
                newMetadata[sectionIndex].fields = arrayMoveImmutable(
                    newMetadata[sectionIndex].fields,
                    oldIndex,
                    newIndex,
                );
                updateData({ ...data, metadata: newMetadata });
            }
        }
    };

    const onSortSectionEnd = ({ oldIndex, newIndex, collection }: any, e: any) => {
        processSort(oldIndex, newIndex, collection);
        setIsActiveDrag(false);
    };
    const onSortFieldEnd = ({ oldIndex, newIndex, collection }: any, e: any) => {
        processSort(oldIndex, newIndex, collection);
        setIsActiveDrag(false);
    };

    const onSortSectionStart = ({ oldIndex, newIndex, collection }: any) => {
        setIsActiveDrag(true);
    };
    const onSortFieldStart = ({ oldIndex, newIndex, collection }: any) => {
        setIsActiveDrag(true);
    };

    const onChangeFieldView = (
        view: string,
        value: boolean,
        sectionIndex: number,
        fieldBlockIndex: number,
        fieldIndex: number,
    ) => {
        let newMetadata: any = [...data.metadata];
        newMetadata[sectionIndex]['fields'][fieldBlockIndex][fieldIndex]['display'][view] = value;
        updateData({ ...data, metadata: newMetadata });
    };

    const onChangeAllFieldsView = (view: string, value: boolean, sectionIndex: number) => {
        let newMetadata = [...data.metadata];
        newMetadata[sectionIndex]['fields'] = newMetadata[sectionIndex]['fields'].map((fieldBlock: any) => {
            return fieldBlock.map((field: any) => {
                field['display'][view] = field['display'][view] != null ? value : null;
                return field;
            });
        });
        updateData({ ...data, metadata: newMetadata });
    };

    let metadataSettings = useMemo(() => {
        return prepareMetadataSettings(data, ownersConfiguration, 'info');
    }, [JSON.stringify(data)]);

    const elementData: IElementData = metadataSettingsPreviewData;

    if (metadataSettings.fields['thumbnail']?.display) {
        metadataSettings.fields['thumbnail'].display = false;
    }
    const elementDataSettings: IElementDataSettings = {
        metadata: metadataSettings,
    };

    return (
        <Stack className={'form-components-indent'}>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <FormControlLabel
                    label={t('display_sections_for_info_tooltip_label')}
                    className={'form-switch'}
                    sx={{
                        alignSelf: 'flex-start',
                    }}
                    control={
                        <Switch
                            data-test={'display_sections_for_info_tooltip'}
                            name={'display_sections_for_info_tooltip'}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                const newSettings = { ...data.settings };
                                newSettings.showSectionHeaders = checked;
                                updateData({ ...data, settings: newSettings });
                            }}
                            checked={data.settings.showSectionHeaders}
                        />
                    }
                />
                <Box>
                    <TemplateFormComponent
                        elementProps={elementProps as any}
                        component={'RestoreDefaultsFormButton'}
                        name={'restore_default_metadata'}
                        props={{
                            buttonLabel: 'form_brand_theme_button_restore',
                            reloadFields: ['brand_theme_tile_element_metadata'],
                            url: `/data/editor/brand-theme/${brandThemeId}/restore-defaults/metadata`,
                            onRestore: () => {
                                const defaultValues = controlProps.form.hookFormGetValues('default_values');
                                controlProps.form.hookFormSetValue(
                                    'brand_theme_tile_element_metadata',
                                    defaultValues.brand_theme_tile_element_metadata,
                                );
                                setData(JSON.parse(defaultValues.brand_theme_tile_element_metadata));
                            },
                        }}
                    />
                </Box>
            </Stack>
            <Box sx={{ position: 'relative' }}>
                <Box
                    className={`metadata-container ${isActiveDrag ? 'metadata-container--active-drag' : ''}`}
                    sx={styles.metadataContainer}
                >
                    <Stack direction="row" sx={styles.metadataHeadingBlock}>
                        <Box sx={styles.metadataHeadingBlockTitle}>{t('metadata.heading_title')}</Box>
                        <Box sx={styles.metadataHeadingBlockColumnIcon}>
                            <Tooltip placement="top" title={t('metadata.heading_tooltip.info')}>
                                <Box sx={{ display: 'inline-flex' }}>
                                    <IconMi icon={'info-circle'} />
                                </Box>
                            </Tooltip>
                        </Box>
                        <Box sx={styles.metadataHeadingBlockColumnIcon}>
                            <Tooltip placement="top" title={t('metadata.heading_tooltip.viewer')}>
                                <Box sx={{ display: 'inline-flex' }}>
                                    <IconMi icon={'dataset-view'} />
                                </Box>
                            </Tooltip>
                        </Box>
                        <Box sx={styles.metadataHeadingBlockColumnIcon}>
                            <Tooltip placement="top" title={t('metadata.heading_tooltip.concierge')}>
                                <Box sx={{ display: 'inline-flex' }}>
                                    <IconMi icon={'concierge'} />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Stack>
                    <SortableSectionsList
                        onSortEnd={onSortSectionEnd}
                        onSortStart={onSortSectionStart}
                        useDragHandle
                        lockAxis={'y'}
                    >
                        {data.metadata.map((section: any, index: number) => {
                            return (
                                <SortableSectionItem
                                    key={section.name}
                                    index={index}
                                    collection={'main_sections'}
                                    data={section}
                                    sectionIndex={index}
                                    onSortFieldEnd={onSortFieldEnd}
                                    onSortFieldStart={onSortFieldStart}
                                    onChangeFieldView={onChangeFieldView}
                                    onChangeAllFieldsView={onChangeAllFieldsView}
                                />
                            );
                        })}
                    </SortableSectionsList>
                </Box>

                <Box sx={styles.metadataInfoPreview}>
                    <Typography variant="heading" sx={{ mb: 2 }}>
                        {t('preview_label')}
                    </Typography>
                    <Box sx={styles.metadataInfoPreviewTooltip}>
                        <ElementDataInfo
                            elementData={elementData}
                            elementDataSettings={elementDataSettings}
                            showHeader={elementDataSettings.metadata.settings.showSectionHeaders}
                        />
                    </Box>
                </Box>
            </Box>
        </Stack>
    );
}

export class FormMetadataSettingsBuilder extends FormComponentBuilder {
    prepareProps(): FormControlMetadataSettingsProps {
        return { ...this.controlProps };
    }
}
